import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import api from "common/api";
import { ICourse, SubjectKind } from "common/types";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { MdClear } from "react-icons/md";
import DeleteCourseConfirmation from "./DeleteCourseConfirmation";
import Spinner from "../../../spinner";
import { toast } from "react-toastify";
import { BiEditAlt } from "react-icons/bi";
import usePageTitle from "common/usePageTitle";

function CoursesAdminListPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<ICourse[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState<boolean>(false);

  usePageTitle("Grupe admin");

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      try {
        const response = await api.get("/courses/");
        const preparationCourses = response.data
          .filter(
            (course: ICourse) =>
              course.subject?.kind === SubjectKind.PREPARATION,
          )
          .filter(
            (course: ICourse) =>
              !course.title?.toLowerCase().includes("monri test grupa"),
          );
        setCourses(preparationCourses);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  async function deleteCourse(id: number) {
    try {
      await api.delete(`/courses/${id}/`);
      setCourses(courses.filter((course) => course.id !== id));
      toast.success("Grupa obrisana");
    } catch (error) {
      console.log(error);
      toast.error("Greška prilikom brisanja grupe");
    }
  }

  const columns = [
    {
      name: "Naziv",
      selector: (row: ICourse) => row.title,
      format: (row: ICourse) => <span title={row.title}>{row.title}</span>,
      sortable: true,
      grow: 1,
    },
    {
      name: "Termin",
      selector: (row: ICourse) => row.times,
      format: (row: ICourse) => <span title={row.times}>{row.times}</span>,
      wrap: true,
      sortable: true,
      grow: 2,
    },
    {
      name: "Broj učenika",
      selector: (row: ICourse) => row.students_count,
      sortable: true,
      grow: 1,
    },
    {
      name: "Predmet",
      grow: 1,
      sortable: false,
      cell: (row: ICourse) => (
        <div>
          <span
            key={row.subject?.id ?? row.id}
            style={{
              padding: "4px 8px",
              marginRight: "5px",
              backgroundColor: "#8d8d8d",
              color: "white",
              borderRadius: "5px",
            }}
          >
            {row.subject?.name ?? "Nepoznat predmet"}
          </span>
        </div>
      ),
    },
    {
      button: true,
      grow: 1,
      cell: (row: ICourse) => (
        <>
          <Link to={`/grupe-admin/edit/${row.id}`}>
            <BiEditAlt
              className="me-3"
              style={{ color: "darkgreen", cursor: "pointer" }}
              title={"Izmijeni grupu"}
            />
          </Link>

          <DeleteCourseConfirmation
            onDeleteConfirm={() => deleteCourse(row.id)}
            course={row}
          />
        </>
      ),
    },
  ];

  const handleRowClick = (row: ICourse) => {
    navigate(`/grupa/${row.id}`);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const lowerSearch = search.toLowerCase();
  const filteredCourses = courses.filter((course) => {
    const matchesTitle = course.title.toLowerCase().includes(lowerSearch);
    const subjectName = course.subject?.name ?? "";
    const matchesSubject = subjectName.toLowerCase().includes(lowerSearch);
    return matchesTitle || matchesSubject;
  });

  return (
    <div>
      <Row className="align-items-center mb-4">
        <Col>
          <h1>Grupe</h1>
        </Col>

        <Col xs="auto" xl={5}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Pretraži grupe..."
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <InputGroup.Text
                style={{ cursor: "pointer" }}
                onClick={() => setSearch("")}
                title="Obriši pretragu"
              >
                <MdClear />
              </InputGroup.Text>
            )}
          </InputGroup>
        </Col>

        <Col xs="auto" xl={5}>
          <Button variant="primary" onClick={() => navigate("/dodaj-grupu")}>
            Dodaj grupu
          </Button>
        </Col>
      </Row>
      <DataTable
        columns={columns}
        data={filteredCourses}
        onRowClicked={handleRowClick}
        progressPending={loading}
        progressComponent={<Spinner />}
        pointerOnHover
        highlightOnHover
        pagination
        noDataComponent="Ne postoje grupe"
      />
    </div>
  );
}

export default CoursesAdminListPage;
